import { PaperAirplaneIcon } from "@heroicons/react/24/solid";
import { clsx } from "clsx";
import { forwardRef, type ReactNode, type TextareaHTMLAttributes, useImperativeHandle, useRef } from "react";
import { useTranslation } from "react-i18next";

import Button from "@/components/common/Button";

interface ChatInputProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  onSend: (question: string) => void;
  // Blocks sending while an answer streams; typing stays allowed.
  sendDisabled?: boolean;
  // Rendered right after the textarea, so peer-* variants work on overlays.
  children?: ReactNode;
}

export const isSendKey = ({ key, shiftKey }: { key: string; shiftKey: boolean }) => key === "Enter" && !shiftKey;

// Height follows the text until max-h-40 stops it, then the textarea scrolls.
// scrollHeight excludes borders, so they are added back for border-box sizing.
export function fitToContent(
  el: Pick<HTMLTextAreaElement, "style" | "scrollHeight" | "offsetHeight" | "clientHeight">,
) {
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
}

// Question box for the page and the chat popup. Long text wraps instead of
// scrolling sideways, and pr-11 keeps it clear of the send button.
const ChatInput = forwardRef<HTMLTextAreaElement, ChatInputProps>(function ChatInput(
  { onSend, sendDisabled = false, className, children, disabled, ...textareaProps },
  ref,
) {
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  useImperativeHandle(ref, () => textareaRef.current!);

  const send = () => {
    const textarea = textareaRef.current;
    if (!textarea || sendDisabled || !textarea.value.trim()) {
      return;
    }
    onSend(textarea.value);
    textarea.value = "";
    fitToContent(textarea);
  };

  return (
    <div className="flex flex-grow relative items-center">
      <textarea
        {...textareaProps}
        ref={textareaRef}
        rows={1}
        enterKeyHint="send"
        disabled={disabled}
        className={clsx(
          "peer flex-grow min-w-0 max-h-40 resize-none overflow-y-auto rounded-md p-2 pr-11 disabled:bg-gray-100 disabled:cursor-not-allowed",
          className,
        )}
        onInput={(e) => fitToContent(e.currentTarget)}
        onKeyDown={(e) => {
          if (isSendKey(e)) {
            e.preventDefault();
            send();
          }
        }}
      />
      {children}
      <Button
        type="button"
        variant="outline"
        aria-label={t("chat.send")}
        disabled={disabled || sendDisabled}
        className="absolute right-0.5 bottom-0.5 !p-2 !border-0 !bg-transparent !shadow-none !text-dobu-orange peer-placeholder-shown:!text-gray-300"
        // Keeps focus in the textarea, so the mobile keyboard stays open.
        onMouseDown={(e) => e.preventDefault()}
        onClick={send}
      >
        <PaperAirplaneIcon className="w-6 h-6" />
      </Button>
    </div>
  );
});

export default ChatInput;
